import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from './AuthContext'; // Import useAuth hook

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth(); // Use auth context
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
    subscribeNewsletter: false
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: ''
  });
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Redirect authenticated users
  useEffect(() => {
    if (isAuthenticated && !loading) {
      const redirectTo = location.state?.from || '/dashboard';
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location.state]);

  // Auth service class
  class AuthService {
    constructor() {
      this.baseURL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';
    }

    async request(endpoint, options = {}) {
      const url = `${this.baseURL}${endpoint}`;
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      };

      try {
        const response = await fetch(url, config);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Something went wrong');
        }

        return data;
      } catch (error) {
        console.error('API Error:', error);
        throw error;
      }
    }

    async register(userData) {
      return this.request('/auth/register', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
    }

    async socialLogin(provider, idToken) {
      return this.request(`/auth/social-login/${provider}`, {
        method: 'POST',
        body: JSON.stringify({ accessToken: idToken }),
      });
    }
  }

  const authService = new AuthService();

  // Initialize Google OAuth
  useEffect(() => {
    const initializeGoogleAuth = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: import.meta.env.REACT_APP_GOOGLE_CLIENT_ID || "760038216153-0bqu43e35jvl486h6kago349g4s5ip9l.apps.googleusercontent.com",
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true
        });

        // Render the Google Sign-In button
        window.google.accounts.id.renderButton(
          document.getElementById("google-register-button"),
          { 
            theme: "outline", 
            size: "large",
            text: "signup_with",
            shape: "rectangular",
            width: "100%"
          }
        );
      }
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleAuth;
      document.head.appendChild(script);
    } else {
      initializeGoogleAuth();
    }

    // Load Facebook SDK
    if (!window.FB) {
      window.fbAsyncInit = function() {
        window.FB.init({
          appId: import.meta.env.REACT_APP_FACEBOOK_APP_ID,
          cookie: true,
          xfbml: true,
          version: 'v18.0'
        });
      };

      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      document.head.appendChild(script);
    }
  }, []);

  // Google OAuth callback handler
  const handleGoogleResponse = async (response) => {
    try {
      setIsLoading(true);
      // console.log('Google response received:', response);

      const result = await authService.socialLogin('google', response.credential);

      if (result.success) {
        // Store the token and user data manually for social login
        sessionStorage.setItem('auth_token', result.token);
        sessionStorage.setItem('user_data', JSON.stringify(result.data.user));
        
        // Force auth context to recheck by reloading
        window.location.reload();
      }
    } catch (error) {
      console.error('Google registration failed:', error);
      setErrors({ 
        submit: error.message || 'Gagal mendaftar dengan Google. Silakan coba lagi.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Facebook Login Handler
  const handleFacebookLogin = () => {
    if (!window.FB) {
      setErrors({ 
        submit: 'Facebook login tidak tersedia. Silakan muat ulang halaman.' 
      });
      return;
    }

    window.FB.login(async (response) => {
      if (response.authResponse) {
        try {
          setIsLoading(true);
          const result = await authService.socialLogin('facebook', response.authResponse.accessToken);
          
          if (result.success) {
            // Store the token and user data manually for social login
            sessionStorage.setItem('auth_token', result.token);
            sessionStorage.setItem('user_data', JSON.stringify(result.data.user));
            
            // Force auth context to recheck
            window.location.reload();
          }
        } catch (error) {
          console.error('Facebook registration failed:', error);
          setErrors({ 
            submit: error.message || 'Gagal mendaftar dengan Facebook. Silakan coba lagi.' 
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        // console.log('Facebook registration cancelled');
      }
    }, { scope: 'email,public_profile' });
  };

  // Form input change handler
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (errors.submit) {
      setErrors(prev => ({ ...prev, submit: '' }));
    }
    if (name === 'password') {
      checkPasswordStrength(value);
    }
  };

  // Password strength checker
  const checkPasswordStrength = (password) => {
    let score = 0;
    let feedback = '';

    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    switch (score) {
      case 0:
      case 1:
        feedback = 'Sangat lemah';
        break;
      case 2:
        feedback = 'Lemah';
        break;
      case 3:
        feedback = 'Sedang';
        break;
      case 4:
        feedback = 'Kuat';
        break;
      case 5:
        feedback = 'Sangat kuat';
        break;
      default:
        feedback = '';
    }

    setPasswordStrength({ score, feedback });
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Nama lengkap wajib diisi';
    } else if (formData.fullName.length < 2) {
      newErrors.fullName = 'Nama lengkap minimal 2 karakter';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email wajib diisi';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Format email tidak valid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Nomor telepon wajib diisi';
    } else if (!/^(\+62|62|0)8[1-9][0-9]{6,9}$/.test(formData.phone)) {
      newErrors.phone = 'Format nomor telepon tidak valid';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password wajib diisi';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password minimal 8 karakter';
    } else if (passwordStrength.score < 3) {
      newErrors.password = 'Password terlalu lemah';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Konfirmasi password wajib diisi';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Password tidak cocok';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'Anda harus menyetujui syarat dan ketentuan';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const registrationData = {
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        password: formData.password,
        confirmPassword: formData.confirmPassword
      };

      const response = await authService.register(registrationData);

      if (response.success) {
        setRegistrationSuccess(true);
        setSuccessMessage(response.message || 'Registrasi berhasil! Silakan cek email Anda untuk verifikasi akun.');

        setFormData({
          fullName: '',
          email: '',
          phone: '',
          password: '',
          confirmPassword: '',
          agreeToTerms: false,
          subscribeNewsletter: false
        });

        // Redirect to login after 5 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Registrasi berhasil! Silakan login dengan akun Anda.' 
            }
          });
        }, 5000);
      }
    } catch (error) {
      console.error('Registration failed:', error);

      if (error.message.includes('Email sudah terdaftar')) {
        setErrors({ email: 'Email sudah terdaftar, gunakan email lain atau login.' });
      } else if (error.message.includes('Data tidak valid')) {
        setErrors({ submit: 'Data yang dimasukkan tidak valid. Periksa kembali form Anda.' });
      } else if (error.message.includes('Gagal mengirim email')) {
        setErrors({ submit: 'Gagal mengirim email verifikasi. Silakan coba lagi atau hubungi customer service.' });
      } else {
        setErrors({ submit: error.message || 'Terjadi kesalahan saat registrasi. Silakan coba lagi.' });
      }
    }

    setIsLoading(false);
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength.score) {
      case 0:
      case 1:
        return 'bg-red-500';
      case 2:
        return 'bg-orange-500';
      case 3:
        return 'bg-yellow-500';
      case 4:
        return 'bg-blue-500';
      case 5:
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full"></div>
          <p className="text-gray-300">Memverifikasi akses...</p>
        </div>
      </div>
    );
  }

  // Jika registrasi sukses, tampilkan pesan sukses
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-green-500/20 border border-green-500/50 rounded-3xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Registrasi Berhasil!</h2>
            <p className="text-gray-300 mb-6">{successMessage}</p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-300 text-center"
              >
                Kembali ke Login
              </Link>
            </div>
            <p className="text-sm text-gray-400 mt-4">
              Anda akan diarahkan ke halaman login dalam 5 detik...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render form registrasi
  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <style>{`
        .card-gradient {
          background: #ffffffcc;
          border: 1px solid rgba(30, 155, 76, 0.3);
          backdrop-filter: blur(10px);
        }
        .input-field {
          background: rgba(255, 255, 255, 0.7);
          border: 1px solid rgba(30, 155, 76, 0.4);
          transition: all 0.3s ease;
        }
        .input-field:focus {
          border-color: rgba(30, 155, 76, 0.7);
          box-shadow: 0 0 0 3px rgba(30, 155, 76, 0.2);
          background: #fff;
        }
        .btn-gradient {
          background: linear-gradient(135deg, #1E9B4C, #136d35);
        }
        .btn-gradient:hover {
          background: linear-gradient(135deg, #136d35, #1E9B4C);
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(30, 155, 76, 0.3);
        }
      `}</style>

      <section className="hero-gradient py-20" style={{
        backgroundImage: "url('https://telnet.net.id/assets/bg.png')",
        backgroundColor:  '#dfe4e0ff',
        backgroundBlendMode: 'overlay',
      }}>
        <div className="max-w-md mx-auto px-4">
          <div className="text-center mb-8">
            <img
              src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png"
              alt="Icon Image"
              className="w-42 h-32 object-contain mx-auto"
              style={{ maxWidth: '70%' }}
            />
            <h1 className="text-4xl font-bold text-black mb-4">Daftar Akun Baru</h1>
            <p className="text-xl text-black">Bergabunglah dengan kami dan nikmati layanan internet terbaik</p>
          </div>

          <div className="card-gradient rounded-3xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Messages */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{errors.submit}</span>
                  </p>
                </div>
              )}

              {/* Full Name */}
              <div className="space-y-2">
                <label htmlFor="fullName" className="block text-sm font-medium text-black">Nama Lengkap</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="input-field w-full px-4 py-3 rounded-xl text-black placeholder-gray-400 focus:outline-none"
                  placeholder="Masukkan nama lengkap Anda"
                  disabled={isLoading}
                />
                {errors.fullName && <p className="text-red-400 text-sm">{errors.fullName}</p>}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-black">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field w-full px-4 py-3 rounded-xl text-black placeholder-gray-400 focus:outline-none"
                  placeholder="nama@email.com"
                  disabled={isLoading}
                />
                {errors.email && <p className="text-red-400 text-sm">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label htmlFor="phone" className="block text-sm font-medium text-black">Nomor Telepon</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input-field w-full px-4 py-3 rounded-xl text-black placeholder-gray-400 focus:outline-none"
                  placeholder="08123456789"
                  disabled={isLoading}
                />
                {errors.phone && <p className="text-red-400 text-sm">{errors.phone}</p>}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-black">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="input-field w-full px-4 py-3 rounded-xl text-black placeholder-gray-400 focus:outline-none pr-12"
                    placeholder="Buat password yang kuat"
                    disabled={isLoading}
                  />
                 <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 hover:text-[#1E9B4C] transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
                </div>
                {/* Password strength bar */}
                {formData.password && (
                  <div className="flex items-center space-x-2 mt-1">
                    <div className="flex-1 bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                        style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score <= 2 ? 'text-red-400' :
                      passwordStrength.score === 3 ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {passwordStrength.feedback}
                    </span>
                  </div>
                )}
                {errors.password && <p className="text-red-400 text-sm">{errors.password}</p>}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-black">Konfirmasi Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="input-field w-full px-4 py-3 rounded-xl text-black placeholder-gray-400 focus:outline-none pr-12"
                    placeholder="Ulangi password Anda"
                    disabled={isLoading}
                  />
                 <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 hover:text-[#1E9B4C] transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                </button>
                </div>
                {formData.confirmPassword && (
                  <div className={`text-sm mt-1 ${formData.password === formData.confirmPassword ? 'text-green-400' : 'text-red-400'}`}>
                    {formData.password === formData.confirmPassword ? 'Password cocok' : 'Password tidak cocok'}
                  </div>
                )}
                {errors.confirmPassword && <p className="text-red-400 text-sm">{errors.confirmPassword}</p>}
              </div>

              {/* Terms and Newsletter */}
              <div className="space-y-4">
                <div className="flex items-start">
                  <input
                    id="agreeToTerms"
                    name="agreeToTerms"
                    type="checkbox"
                    checked={formData.agreeToTerms}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-800 bg-gray-700 border-black rounded focus:ring-blue-600 focus:ring-2 mt-1"
                    disabled={isLoading}
                  />
                  <label htmlFor="agreeToTerms" className="ml-3 text-sm text-black">
                    Saya menyetujui{' '}
                    <Link to="/terms" className="text-blue-600 hover:text-blue-500 transition-colors">
                      Syarat dan Ketentuan
                    </Link>{' '}
                    serta{' '}
                    <Link to="/privacy" className="text-blue-600 hover:text-blue-500 transition-colors">
                      Kebijakan Privasi
                    </Link>
                  </label>
                </div>
                {errors.agreeToTerms && <p className="text-red-400 text-sm">{errors.agreeToTerms}</p>}

                <div className="flex items-center">
                  <input
                    id="subscribeNewsletter"
                    name="subscribeNewsletter"
                    type="checkbox"
                    checked={formData.subscribeNewsletter}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-700 bg-gray-700 border-gray-600 rounded focus:ring-blue-600 focus:ring-2"
                    disabled={isLoading}
                  />
                  <label htmlFor="subscribeNewsletter" className="ml-3 text-sm text-black">
                    Saya ingin menerima newsletter dan update promo terbaru
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Sedang mendaftar...</span>
                  </div>
                ) : (
                  'Daftar Sekarang'
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-600">atau</span>
                </div>
              </div>

              {/* Social Login */}
              <div className="space-y-3">
                {/* Google Sign-In Button */}
                <div id="google-register-button" className="w-full"></div>

                {/* Facebook */}
                <a
                href={`https://www.facebook.com/v12.0/dialog/oauth?client_id=YOUR_APP_ID&redirect_uri=YOUR_REDIRECT_URI&scope=email,public_profile&response_type=code`}
                className="w-full px-6 py-3 bg-[#1877F2] text-white font-semibold rounded-xl hover:bg-[#166FE5] transition-all duration-300 flex items-center justify-center space-x-2"
                >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span>Daftar dengan Facebook</span>
              </a>
              </div>

              {/* Link to Login */}
              <p className="text-center text-gray-600 mt-6">
                Sudah punya akun?{' '}
                <Link to="/login" className="text-[#1E9B4C] hover:text-[#136d35] font-semibold transition-colors">
                  Masuk di sini
                </Link>
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RegisterPage;